import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Loader from '../../../../../services/loader';
import Notifications from '../../../../../services/notifications';
import ModalHeader from '../../../../../commonComponents/modal/ModalHeader';
import TextFilter from '../../../../../commonComponents/lists/filters/TextFilter';
import ScrollContainer from '../../../../../commonComponents/container/ScrollContainer';
import ModalFooter from '../../../../../commonComponents/modal/ModalFooter';
import translate from '../../../../../commonComponents/translate';
import Modal from '../../../../../commonComponents/modal/Modal';
import { compose } from '../../../../../services/utilityFunctions';
import ProductProp, { PACK_TYPE } from '../../../propTypes/productProp';
import PackItem from './PackItem';
import UserProp from '../../../../../commonComponents/propTypes/userProp';
import ProductVariationsProp from '../../../propTypes/productVariationsProp';
import MarketInfosContext from '../../../../../contextProviders/MarketInfosContext';

function AddPackProduct(props) {
  const {
    t,
    product,
    endpoint,
    user,
    onClose,
    selectedVariations,
    isAvailableEmpty,
    packVariationsAvailable,
    multiPackVariationsAvailable,
    isBySection = false,
    stock: stockProp,
    amountVariationsToChoose: amountVariationsToChooseProp,
  } = props;
  const { _id: productId, pack = {} } = product || {};
  const { amountVariationsPerAdvancedPack, packType } = pack || {};
  const { marketInfos } = useContext(MarketInfosContext);
  const currency = user?.currency || marketInfos.localCurrency;

  // Loader ref
  const loader = useRef(new Loader('#product-list'));

  // State so we know if it's needed to get more items
  const [metaInfoTotal, setMetaInfoTotal] = useState(null);
  const [page, setPage] = useState(1);

  // All products to display to the user
  const [allVariations, setAllVariations] = useState([]);
  // Filters state
  const [filters, setFilters] = useState({});
  const updateQuantity = useCallback(
    (productVariationId, sectionId) => qty => {
      setAllVariations(prevVariations =>
        prevVariations?.map(variation =>
          variation?._id === productVariationId &&
          (isBySection ? variation.sectionId === sectionId : true)
            ? {
                ...variation,
                quantity: parseInt(qty, 10),
                inside:
                  (variation?.quantity || 0) < qty
                    ? (variation?.quantity || 0) < qty
                    : variation?.inside,
              }
            : variation,
        ),
      );
    },
    [setAllVariations],
  );

  const stock = useMemo(() => {
    if (stockProp) return stockProp;

    if (isBySection && allVariations?.length > 0) {
      return allVariations.reduce((acc, item) => {
        const id = item.variation?._id || item._id;
        if (!acc[id]) {
          const stockQuantity =
            item.variation?.stockQuantity ?? item.stockQuantity;
          acc[id] = {
            quantity: item.quantity || 0,
            stockQuantity: stockQuantity ?? null,
          };
        } else {
          acc[id].quantity += item.quantity || 0;
        }

        return acc;
      }, {});
    }
    return null;
  }, [stockProp, isBySection, allVariations]);

  // region Handles popup exit
  /**
   * Handles the "exit" of the popup, can be with or without products
   * If isAvailableEmpty means that is an hybrid pack and there is no available products to add
   * @products {MarketProductVariations[]} - variations to add
   */
  const handleClose = (action, products) => {
    if (action === 'add') {
      const productsInside = products?.filter(p => p.inside && p.quantity > 0);
      if (productsInside?.length === 0 && !isAvailableEmpty) {
        Notifications.showNotificationWarning(
          t('warning'),
          t('advancedPack.error.amountVariationsPerAdvancedPack.product'),
        );
        return;
      }

      const numberOfProducts =
        productsInside?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;

      if (
        packType !== PACK_TYPE.HYBRID_PACK &&
        amountVariationsPerAdvancedPack !== numberOfProducts &&
        !isAvailableEmpty
      ) {
        Notifications.showNotificationWarning(
          t('warning'),
          t(
            'advancedPack.error.amountVariationsPerAdvancedPack',
            amountVariationsPerAdvancedPack,
          ),
        );
        return;
      }

      if (packType === PACK_TYPE.HYBRID_PACK && !isAvailableEmpty) {
        const allSectionsAreValid = multiPackVariationsAvailable?.every(
          section => {
            const {
              packVariationsAvailable: sectionIds,
              amountVariationsToChoose,
              name,
              _id,
            } = section;
            const selectedInSection =
              allVariations
                ?.filter(
                  v =>
                    (isBySection
                      ? v.sectionId === _id
                      : sectionIds?.includes(v._id)) &&
                    v.inside &&
                    !v.locked,
                )
                ?.reduce(
                  (acc, item) => acc + parseInt(item.quantity || '0', 10),
                  0,
                ) || 0;

            if (selectedInSection !== amountVariationsToChoose) {
              Notifications.showNotificationWarning(
                t('warning'),
                t('shop.hybrid_pack.warning', amountVariationsToChoose, name),
              );
            }
            return selectedInSection === amountVariationsToChoose;
          },
        );

        if (!allSectionsAreValid) {
          return;
        }
      }

      onClose(productsInside);
    } else {
      onClose(null);
    }
  };
  // endregion

  const disabledSave = useMemo(() => {
    if (isAvailableEmpty) {
      return false;
    }

    if (amountVariationsToChooseProp) {
      const selectedInSection =
        allVariations
          ?.filter(item => item.inside)
          ?.reduce(
            (acc, item) => acc + parseInt(item.quantity || '0', 10),
            0,
          ) || 0;

      return selectedInSection !== amountVariationsToChooseProp;
    }

    if (stock && packType === PACK_TYPE.HYBRID_PACK) {
      return Object.entries(
        allVariations.reduce((acc, item) => {
          const id = item.variation?._id || item._id;
          if (!acc[id]) {
            const stockQuantity =
              item.variation?.stockQuantity ?? item.stockQuantity;
            acc[id] = {
              quantity: item.quantity || 0,
              stockQuantity: stockQuantity ?? null,
            };
          } else {
            acc[id].quantity += item.quantity || 0;
          }
          return acc;
        }, {}),
      ).some(([id, data]) => {
        const stockData = stock?.[id];
        if (!stockData) {
          if (
            parseInt(data.quantity, 10) >
            (data.stockQuantity ? parseInt(data.stockQuantity, 10) : Infinity)
          ) {
            return true;
          }
          return false;
        }
        const totalQuantity = isBySection
          ? stockData.quantity || 0
          : data.quantity + (stockData.quantity || 0);
        return totalQuantity > (data.stockQuantity ?? Infinity);
      });
    }

    if (packType === PACK_TYPE.HYBRID_PACK) {
      // Check if each section has at least amountVariationsToChoose
      const hasValidSections = multiPackVariationsAvailable?.every(section => {
        const {
          packVariationsAvailable: sectionIds,
          amountVariationsToChoose,
        } = section;
        const selectedInSection =
          allVariations
            ?.filter(v => sectionIds?.includes(v._id) && v.inside && !v.locked)
            ?.reduce(
              (acc, item) => acc + parseInt(item.quantity || '0', 10),
              0,
            ) || 0;
        return selectedInSection === amountVariationsToChoose;
      });

      return !hasValidSections;
    }

    return (
      amountVariationsPerAdvancedPack !==
      (allVariations
        ?.filter(v => v.inside)
        ?.reduce((acc, item) => acc + parseInt(item.quantity || '0', 10), 0) ||
        0)
    );
  }, [amountVariationsPerAdvancedPack, allVariations]);

  // region Handles the checkbox click
  /**
   * Handles the event on the checkbox
   * Updates the `inside` property for a list of product variations.
   *
   * @param {Object} variation - The product variation to update.
   * @param {string} variation._id - The unique identifier of the variation to update.
   * @param {boolean} [variation.inside] - The current state of the `inside` property (optional).
   *
   * @callback
   */
  const addProduct = useCallback(
    (variation, sectionId) => {
      setAllVariations(prevState =>
        prevState.map(pVariation => {
          if (
            pVariation._id === variation._id &&
            (isBySection ? pVariation.sectionId === sectionId : true)
          ) {
            return {
              ...pVariation,
              inside: !pVariation?.inside,
              quantity: parseInt(pVariation?.quantity || 1, 10),
            };
          }
          return pVariation;
        }),
      );
    },
    [setAllVariations],
  );
  // endregion Handles the checkbox click

  // region Handles Search Change with debounce
  /**
   * Handles search input with timeout
   * @value {string}
   */
  // Create a ref to store the timeout ID
  const debounceTimeoutRef = useRef(null);
  const handleSearchChange = useCallback(
    value => {
      // Clear the previous timeout if it exists
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set a new timeout with a delay of 400ms
      debounceTimeoutRef.current = setTimeout(() => {
        setFilters(prevState => ({
          ...prevState,
          search: value,
        }));
      }, 400);
    },
    [setFilters],
  );
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    [],
  );
  // endregion Handles Search Change with debounce

  // region HANDLE SCROLLABLE PRODUCTS
  /**
   * Request to API to Get ITEMS
   * @type {(function(number=, {}=): Promise<*|[]|undefined>)|*}
   */
  const fetchItems = async (
    filtersParam = {},
    pageNumber = 1,
    initVariations = [],
  ) => {
    try {
      loader.current.show();
      const url =
        endpoint ||
        `/table/market/product/${productId}/available/variations/list`;

      const params = filtersParam;

      const userCountry = user?.shippingCountry || user?.address?.countryCode;
      if (userCountry) {
        params.country = userCountry;
      }
      params.currency = currency;

      const { data: response } = await axios.get(url, {
        params: {
          pagination: { perpage: 10, page: pageNumber },
          ...filtersParam,
        },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      });
      const { data, meta } = response;
      return {
        pageNumber,
        newProducts: [
          ...initVariations,
          ...data.map(variation => {
            const alreadySelected = selectedVariations.find(
              pVariation => pVariation._id === variation._id,
            );
            return alreadySelected
              ? {
                  ...variation,
                  quantity: alreadySelected.quantity || 0,
                  inside: alreadySelected.inside,
                }
              : variation; // Return the original variation if not already selected
          }),
        ],

        pages: meta?.pages,
      };
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'error_fetchProducts');
      return {
        pageNumber: 1,
        newProducts: [],
        pages: 1,
      };
    } finally {
      loader.current.hide();
    }
  };

  /** region Gets first batch of items for page 1 */
  useEffect(() => {
    (async () => {
      const { newProducts, pages } = await fetchItems(filters, 1, []);
      setMetaInfoTotal(pages);
      if (
        product?.packType === PACK_TYPE.HYBRID_PACK ||
        pack?.packType === PACK_TYPE.HYBRID_PACK
      ) {
        if (!isBySection) {
          setAllVariations([
            ...newProducts.filter(p =>
              packVariationsAvailable?.map(pA => pA._id).includes(p._id),
            ),
            ...(product?.parentProduct?.packVariations?.map(pV => ({
              ...pV.variation,
              inside: true,
              quantity: pV.quantity,
              locked: true,
            })) || []),
          ]);
        } else {
          const filteredProducts = multiPackVariationsAvailable.flatMap(mp =>
            mp.packVariationsAvailable
              .map(variationId => {
                const variation = newProducts.find(p => p._id === variationId);
                return variation
                  ? { ...variation, sectionId: mp._id || mp.name }
                  : null;
              })
              .filter(Boolean),
          );

          setAllVariations([
            ...filteredProducts,
            ...(product?.parentProduct?.packVariations?.map(pV => ({
              ...pV.variation,
              inside: true,
              quantity: pV.quantity,
              locked: true,
            })) || []),
          ]);
        }
      } else {
        setAllVariations(newProducts);
      }
      setPage(1);
    })();
  }, [filters]);

  /**
   * When scroll container gets to the end, we get another batch of items
   * If page is not greater that total of pages
   * And then we update the allVariation state
   * @type {(function(): Promise<void>)|*}
   */
  const isFetchingRef = useRef(false);
  const handleReachedEnd = useCallback(async () => {
    if (isFetchingRef.current) return; // Exit if a fetch is in progress

    isFetchingRef.current = true; // Set fetching status to true

    if (!metaInfoTotal || page + 1 <= metaInfoTotal) {
      const { pageNumber, newProducts, pages } = await fetchItems(
        filters,
        page + 1,
        allVariations,
      );
      setPage(pageNumber);
      setMetaInfoTotal(pages);
      setAllVariations(newProducts);
    }

    isFetchingRef.current = false; // Reset fetching status after fetch is done
  }, [
    setAllVariations,
    setPage,
    setMetaInfoTotal,
    filters,
    metaInfoTotal,
    page,
    allVariations,
  ]);
  // endregion HANDLE SCROLLABLE PRODUCTS

  return (
    <Modal id="addPackProduct">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content packModal">
          <ModalHeader
            title={
              packType === PACK_TYPE.HYBRID_PACK && isBySection
                ? t('shop.hybrid_pack.select')
                : t(
                    'advancedPack.amountOfProducts',
                    amountVariationsPerAdvancedPack,
                  )
            }
          />
          <div className="modal-body">
            <div className="products-filters-wrapper">
              <TextFilter
                id="searchFilter"
                onChange={handleSearchChange}
                placeholder={t('filters.search.products')}
                value={filters?.search}
                isSearch
              />
            </div>
            <div id="product-list">
              <div className="content-wrapper">
                <div className="placeholder">
                  <div className="label" />
                  <span
                    className="name-wrapper"
                    style={{ paddingLeft: '1.7rem' }}
                  >
                    {t('name')}
                  </span>
                  <span className="quantity-wrapper">
                    {t('shop.product.qty')}
                  </span>
                </div>
                {allVariations?.length > 0 ? (
                  <ScrollContainer
                    onReachedEnd={handleReachedEnd}
                    onReachedEndDelay={1500}
                    containerClass="product-list-wrapper"
                  >
                    {isBySection ? (
                      <>
                        {multiPackVariationsAvailable?.map(multiPack => {
                          const {
                            name,
                            packVariationsAvailable:
                              multiplePackVariationsAvailable = [],
                            amountVariationsToChoose = 1,
                          } = multiPack;

                          const sectionVariations =
                            multiplePackVariationsAvailable
                              .map(variationId =>
                                allVariations?.find(
                                  pVariation =>
                                    pVariation._id === variationId &&
                                    pVariation.sectionId === multiPack._id,
                                ),
                              )
                              .filter(Boolean);
                          return (
                            <div key={name}>
                              <h6>
                                {name} -{' '}
                                {t(
                                  'advancedPack.amountOfProducts',
                                  amountVariationsToChoose,
                                )}
                              </h6>
                              {sectionVariations.map(variation => (
                                <PackItem
                                  key={`${variation._id}-${multiPack._id}`}
                                  variation={variation}
                                  onClick={addProduct}
                                  onChange={updateQuantity}
                                  sectionId={multiPack._id}
                                  stock={stock}
                                  isBySection
                                />
                              ))}
                            </div>
                          );
                        })}

                        {(() => {
                          const fixedVariations = allVariations?.filter(
                            variation => variation.locked,
                          );

                          if (!fixedVariations?.length) return null;

                          return (
                            <>
                              <h5>{t('shop.hybrid_pack.fixed')}</h5>
                              {fixedVariations.map(variation => (
                                <PackItem
                                  key={variation._id}
                                  variation={variation}
                                  onClick={addProduct}
                                  onChange={updateQuantity}
                                  stock={stock}
                                  isBySection
                                />
                              ))}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      allVariations?.map(variation => (
                        <PackItem
                          key={variation._id}
                          variation={variation}
                          onClick={addProduct}
                          onChange={updateQuantity}
                          stock={stock}
                        />
                      ))
                    )}
                  </ScrollContainer>
                ) : (
                  <span className="hint-form">{t('noProducts')}</span>
                )}
              </div>
            </div>
          </div>
          <ModalFooter
            onSave={() =>
              handleClose(
                'add',
                allVariations?.filter(v => !v.locked),
              )
            }
            cancelText={t('cancel')}
            onCancel={() => handleClose('cancel', null)}
            saveText={t('add.product')}
            disabledSave={disabledSave}
          />
        </div>
      </div>
    </Modal>
  );
}

AddPackProduct.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType).isRequired,
  endpoint: PropTypes.string,
  user: PropTypes.shape(UserProp.propType).isRequired,
  selectedVariations: PropTypes.arrayOf(
    PropTypes.shape(ProductVariationsProp.propType),
  ),
  packVariationsAvailable: PropTypes.arrayOf(
    PropTypes.shape(ProductVariationsProp.propType),
  ),
  multiPackVariationsAvailable: PropTypes.arrayOf(
    PropTypes.shape({
      packVariationsAvailable: PropTypes.arrayOf(
        PropTypes.shape(ProductVariationsProp.propType),
      ),
    }),
  ),
  isBySection: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  isAvailableEmpty: PropTypes.bool,
  stock: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      stockQuantity: PropTypes.number,
      quantity: PropTypes.number,
    }),
  }),
  amountVariationsToChoose: PropTypes.number,
};

AddPackProduct.defaultProps = {
  endpoint: null,
  selectedVariations: [],
  packVariationsAvailable: [],
  multiPackVariationsAvailable: [],
  isBySection: false,
  isAvailableEmpty: false,
  stock: null,
  amountVariationsToChoose: null,
};

export default compose(translate)(AddPackProduct);
